import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';
import {
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import { FaEdit, FaLink, FaUnlink } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getBankAccountList, archiveSchemeAccount } from '../../../helper/Api';
import { AddCommasToAmount } from '../../../helper/Utils';
import { Link } from 'react-router-dom';
import './AccountList.css';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 88;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_BOTTOM_GAP = 16;

const formatDisplayDate = (value) => {
    if (!value) return '—';
    const parsed = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : value;
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return AddCommasToAmount(value);
};

const isSchemeLinked = (row) =>
    row?.is_scheme_linked === true ||
    row?.is_scheme_linked === 1 ||
    row?.is_scheme_linked === '1';

const ChildAccountList = ({ searchStr, tier, isHolding = false }) => {
    const { authData } = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');
    const tableContainerRef = useRef(null);
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
    const [pendingArchiveAccountId, setPendingArchiveAccountId] = useState(null);
    const [archiving, setArchiving] = useState(false);

    const canEdit =
        authData.user.user_type === 'l3' &&
        authData.user.department_details.department_level === 'T1';
    const canArchive = authData.user.user_type === 'l3' && !isHolding;
    const showActions = canEdit || canArchive;

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px"
            },
        },
    };

    const openArchiveConfirm = (accountId) => {
        if (!accountId) {
            toast.error("Account id is required.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        setPendingArchiveAccountId(accountId);
        setArchiveConfirmOpen(true);
    };

    const closeArchiveConfirm = () => {
        if (archiving) return;
        setArchiveConfirmOpen(false);
        setPendingArchiveAccountId(null);
    };

    const fetchList = async () => {
        try {
            const resp = await getBankAccountList({
                tier: tier,
                searchStr: searchStr,
                department_id: (authData.user.department_id !== "1") ? authData.user.department_id : null,
                is_holding: isHolding ? 1 : 0
            });
            if (resp.data.status) {
                setData(resp.data.result);
            }
        } catch (error) {
            setData([]);
        }
    };

    const confirmArchiveAccount = async () => {
        if (!pendingArchiveAccountId) {
            toast.error("Account id is required.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setArchiving(true);
            const response = await archiveSchemeAccount(pendingArchiveAccountId);

            if (response?.data?.status) {
                toast.success(response.data.message || "Account archived successfully.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setArchiveConfirmOpen(false);
                setPendingArchiveAccountId(null);
                await fetchList();
            } else {
                toast.error(response?.data?.message || "Failed to archive account.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message || error?.message || "Failed to archive account.",
                {
                    position: toast.POSITION.TOP_CENTER,
                }
            );
        } finally {
            setArchiving(false);
        }
    };

    const columns = [
        ...(showActions
            ? [
                  {
                      name: 'Actions',
                      cell: (row) => (
                          <div className="child-account-actions">
                              {canEdit ? (
                                  <Tooltip title="Edit" arrow placement="top">
                                      <span>
                                          <Link
                                              to={
                                                  isHolding
                                                      ? `/editchildAccount/holding/${row.id}`
                                                      : `/editchildAccount/${row.id}`
                                              }
                                              className="child-account-action-btn child-account-action-btn--edit"
                                          >
                                              <FaEdit size={15} />
                                          </Link>
                                      </span>
                                  </Tooltip>
                              ) : null}
                              {canArchive && !isSchemeLinked(row) ? (
                                  <Tooltip title="Archive" arrow placement="top">
                                      <span>
                                          <button
                                              type="button"
                                              className="child-account-action-btn child-account-action-btn--archive"
                                              onClick={() => openArchiveConfirm(row.id)}
                                              aria-label="Archive account"
                                          >
                                              <i className="icon-archive"></i>
                                          </button>
                                      </span>
                                  </Tooltip>
                              ) : null}
                          </div>
                      ),
                      width: "110px",
                      center: true,
                      right: false,
                  },
              ]
            : []),
        {
            name: 'Department',
            selector: (row) => row.name,
            cell: (row) => (
                <div className="child-account-dept-cell">
                    <span className="child-account-dept-name">{row.name || "—"}</span>
                    {row.department_level ? (
                        <span className="child-account-dept-level-badge">
                            {row.department_level}
                        </span>
                    ) : null}
                    {row.code ? (
                        <span className="child-account-dept-code">{row.code}</span>
                    ) : null}
                </div>
            ),
            sortable: true,
            width: "280px",
            wrap: true,
            right: false,
        },
        {
            name: 'Account Name',
            selector: (row) => row.account_name,
            sortable: true,
            width: "400px",
            right: false
        },
        {
            name: 'Account No',
            selector: (row) => row.account_no,
            cell: (row) => {
                const linked = isSchemeLinked(row);
                return (
                    <div className="child-account-account-no-cell">
                        <span className="child-account-account-no">{row.account_no || "—"}</span>
                        <Tooltip
                            title={linked ? 'Linked to scheme' : 'Not linked to scheme'}
                            arrow
                            placement="top"
                        >
                            <span
                                className={`child-account-scheme-link-icon ${
                                    linked
                                        ? 'child-account-scheme-link-icon--linked'
                                        : 'child-account-scheme-link-icon--unlinked'
                                }`}
                                aria-label={linked ? 'Linked to scheme' : 'Not linked to scheme'}
                            >
                                {linked ? <FaLink size={11} /> : <FaUnlink size={11} />}
                            </span>
                        </Tooltip>
                    </div>
                );
            },
            sortable: true,
            width: "220px",
            right: false
        },
        {
            name: 'Account Balance',
            selector: (row) => row.account_balance,
            cell: (row) => formatAmount(row.account_balance),
            sortable: true,
            width: "180px",
            right: true
        },
        {
            name: 'Balance As On',
            selector: (row) => row.balance_date,
            cell: (row) => formatDisplayDate(row.balance_date),
            sortable: true,
            width: "150px",
            right: false
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchList();
    }, [searchStr, tier, isHolding]);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.min(data.length || 1, rowsPerPage);
            const bodyContentHeight = rowCount * TABLE_ROW_HEIGHT;
            const containerTop = tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight = window.innerHeight
                - containerTop
                - TABLE_HEADER_HEIGHT
                - TABLE_PAGINATION_HEIGHT
                - TABLE_BOTTOM_GAP;
            const nextHeight = Math.min(
                bodyContentHeight,
                Math.max(maxBodyHeight, 120)
            );
            setTableScrollHeight(`${nextHeight}px`);
        };

        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, [data.length, rowsPerPage]);

    return (
        <div ref={tableContainerRef} className="child-account-datatable-root">
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={data}
                fixedHeader
                fixedHeaderScrollHeight={tableScrollHeight}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={data.length}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />

            <Dialog
                open={archiveConfirmOpen}
                onClose={closeArchiveConfirm}
                maxWidth="xs"
                fullWidth
                aria-labelledby="archive-account-title"
            >
                <DialogTitle id="archive-account-title">Archive account</DialogTitle>
                <DialogContent dividers>
                    <p className="mb-0" style={{ fontSize: 15, lineHeight: 1.5 }}>
                        Are you sure you want to archive this account record? Once archived,
                        you will not be able to get it back.
                    </p>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={closeArchiveConfirm} color="inherit" disabled={archiving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmArchiveAccount}
                        variant="contained"
                        color="error"
                        disabled={archiving}
                    >
                        {archiving ? "Archiving..." : "Yes, archive"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
export default ChildAccountList
